import styled, { css } from 'styled-components';
import { media } from '@simplywallst/ui-core';
import ChevronRightSVG from '@assets/images/icons/legacy/chevron-right.svg';
import RootDataTable, { Props } from '@components/DataTable';

export const DataTable = styled(RootDataTable)<Props>`
  border-collapse: collapse;
  border-bottom: 0;
  border-left: 0;
  border-right: 0;
  thead tr th {
    border-bottom: 1px solid ${({ theme }) => theme.color.whiteRGBA(0.2)};
    background: transparent;
    ${({ theme }) => theme.typography.tinyStrong};
    color: ${({ theme }) => theme.color.whiteRGBA(0.5)};
  }
  tr {
    height: auto;
  }
  td,
  th {
    padding: ${({ theme }) => `${theme.x1spacePx}px`};
    vertical-align: middle;
  }
  td {
    ${({ theme }) => theme.typography.xSmall};
    padding: 0;
  }
  tbody tr:first-child {
    > td {
      padding-top: ${({ theme }) => theme.x1spacePx / 2}px;
    }
  }
  > tbody > tr {
    > td {
      border: none;
      transition: background 0.2s cubic-bezier(0.23, 1, 0.32, 1);
      > a,
      > span {
        display: block;
        padding: ${({ theme }) => `${theme.x1spacePx}px`};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-decoration: none;
        color: inherit;
      }
    }
    &:hover {
      &:only-child {
        > td {
          pointer-events: none;
          cursor: initial;
          background-color: unset;
          color: unset;
        }
      }
      > td {
        background: ${({ theme }) => theme.color.whiteRGBA(0.05)};
        cursor: pointer;
      }
    }
    &:active {
      > td {
        background: ${({ theme }) => theme.color.whiteRGBA(0.1)};
      }
    }
  }
`;

export const Wrapper = styled('div')`
  margin-top: ${({ theme }) => theme.x2spacePx}px;
  margin-bottom: ${({ theme }) => theme.x2spacePx}px;
`;

export const PeriodTabListWrapper = styled('div')`
  margin: ${({ theme }) => `0 -${theme.x1spacePx}px`};
`;

export const Name = styled('td')`
  && {
    padding-left: ${({ theme }) => `${theme.x1spacePx}px`};
    max-width: 50px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${({ theme }) => theme.typography.xSmall}
  }
`;

export const Value = styled('td')`
  ${({ theme }) => theme.typography.xSmall}
  && {
    padding: 0 ${({ theme }) => `${theme.x1spacePx}px`};
  }
`;

export const BarCell = styled('td')`
  width: 50%;
`;

export const BarWrapper = styled('span')`
  && {
    display: inline-flex;
    padding: ${({ theme }) => theme.x1spacePx / 2}px
      ${({ theme }) => theme.x1spacePx / 2}px
      ${({ theme }) => theme.x1spacePx / 2}px 0;
  }
`;

export const HeadCell = styled('th')`
  &&& {
    text-align: left;
    padding-left: 0;
    height: ${({ theme }) => theme.x6spacePx}px;
    > span {
      ${({ theme }) => theme.typography.xSmall}
      border-bottom: 1px dotted ${({ theme }) => theme.color.brand01};
      cursor: pointer;
      background-color: ${({ theme }) => theme.color.brand01RGBA(0.1)};
      color: ${({ theme }) => theme.color.white};
      user-select: none;
      ${media.smDown`
        max-width: 100px;
      `}
    }
  }
`;

const disabledCSS = css`
  > td {
    cursor: initial !important;
    background-color: unset !important;
    color: unset;
  }
  > td > svg {
    opacity: 0.1;
    * {
      fill: ${({ theme }) => theme.color.white};
    }
  }
`;
const enabledCSS = css`
  > td {
    cursor: pointer;
    background-color: inherit;
    color: inherit;
  }
  > td > svg {
    opacity: 1;
    * {
      fill: ${({ theme }) => theme.color.brand01};
    }
  }
  > td > span {
    svg:last-child {
      opacity: 1;
      * {
        fill: ${({ theme }) => theme.color.brand01};
      }
    }
  }
`;

interface TableRowProps {
  disabled?: boolean;
  current?: boolean;
  isChild?: boolean;
}

export const TableRow = styled('tr')<TableRowProps>`
  border-left: ${({ theme, current }) =>
    current ? `2px solid ${theme.color.brand01}` : 'none'};
  > td {
    border: none;
    background: ${({ current, theme }) => {
      if (current === true) {
        return `${theme.color.whiteRGBA(0.05)} !important`;
      }
    }};
    cursor: ${({ current }) => (current === true ? 'default !important' : '')};
    :first-child {
      padding-left: ${({ theme, isChild }) =>
        isChild ? theme.x2spacePx : theme.x1spacePx}px !important;
    }
  }
  > th {
    border-bottom: 1px solid ${({ theme }) => theme.color.whiteRGBA(0.2)} !important;
  }
  &:hover {
    ${(props) => (props.disabled ? disabledCSS : enabledCSS)}
    > td {
      background: ${({ current, theme }) => {
        if (current === true) {
          return `${theme.color.whiteRGBA(0.05)} !important`;
        }
      }};
    }
  }
  &:last-child {
    &&& {
      text-align: center;
    }
  }
`;

export const LastCell = styled('td')`
  &&& {
    text-align: center;
    vertical-align: middle;
    padding-top: ${({ theme }) => theme.x1spacePx / 2}px;
  }
`;

type ChevronRightProps = {
  opacity?: string;
};

export const ChevronRight = styled(ChevronRightSVG)<ChevronRightProps>`
  opacity: ${({ opacity }) => opacity || 0.3};
  width: ${({ theme }) => theme.x2spacePx - 4}px;
  height: ${({ theme }) => theme.x2spacePx + 4}px;
  margin: 0 ${({ theme }) => theme.x1spacePx}px;
  > * {
    fill: white;
  }
`;

export const NoResults = styled('td')`
  &&& {
    text-align: center !important;
    padding: ${({ theme }) => theme.x5spacePx}px 0;
    border-bottom: none;
  }

  ${({ theme }) => theme.typography.xSmall};
  opacity: 0.3;
`;
